/*
Finds all Queries that exist within expression.
E.g. for expression `patient()["field"] + previousVisit()["field"] + firstBlockInCase("PSG")["field"]`
it will return array of 3 elements:
[
  { query: {queryType: 'Patient', dataBlockTypeId: null}, filter: null, limit: null },
  { query: {queryType: 'Visit', dataBlockTypeId: null}, filter: null, limit: null },
  { query: {queryType: 'DataBlockQuery', dataBlockTypeId: 10}, filter: null, limit: 'firstBlockInCase'},
]
This is needed to determine all distinct queries within a document.
 */
import {
  MergeFieldQueryType,
  ExpressionDataFunction,
  QueryWithParameters,
} from './types';
import { visitExpression } from './helpers/expression-visitor';
import { ExpressionVisitor } from './helpers/ExpressionVisitor';

let _selectVisitsFunction: () => Promise<number[]> | null;
export function setSelectVisitsFunctionInternal(func: () => Promise<number[]>) {
  _selectVisitsFunction = func;
}

function getFields(visitor: ExpressionVisitor, functionName: string) {
  return [
    ...new Set(
      visitor.getMembers
        .filter((x) => x.leftPart === `${functionName}()`)
        .map((x) => x.memberName)
    ),
  ];
}

export async function extractQueriesFromExpression(
  expression: string
): Promise<QueryWithParameters[]> {
  const visitor = new ExpressionVisitor();
  visitExpression(expression, visitor);

  let selectedVisits: number[] | null = null;

  const result: QueryWithParameters[] = [];
  for (const func of visitor.functionCalls) {
    if (
      func.functionName.toLowerCase() ===
      ExpressionDataFunction.patient.toLowerCase()
    ) {
      const fields = getFields(visitor, 'patient');

      result.push({
        query: {
          queryType: MergeFieldQueryType.PatientQuery,
          dataBlockTypeName: null,
          selectedVisits: selectedVisits,
        },
        fields: fields,
        filter: '',
        limit: null,
        expressionFunctionName: ExpressionDataFunction.patient,
        expressionFunctionArgument: '',
      });
    } else if (
      func.functionName.toLowerCase() ===
      ExpressionDataFunction.Case.toLowerCase()
    ) {
      const fields = getFields(visitor, 'Case');

      result.push({
        query: {
          queryType: MergeFieldQueryType.CaseQuery,
          dataBlockTypeName: null,
          selectedVisits: selectedVisits,
        },
        fields: fields,
        filter: '',
        limit: null,
        expressionFunctionName: ExpressionDataFunction.Case,
        expressionFunctionArgument: '',
      });
    } else if (
      func.functionName.toLowerCase() ===
      ExpressionDataFunction.visit.toLowerCase()
    ) {
      const fields = getFields(visitor, 'visit');

      result.push({
        query: {
          queryType: MergeFieldQueryType.VisitQuery,
          dataBlockTypeName: null,
          selectedVisits: selectedVisits,
        },
        fields: fields,
        filter: '',
        limit: null,
        expressionFunctionName: ExpressionDataFunction.visit,
        expressionFunctionArgument: '',
      });
    } else if (
      func.functionName.toLowerCase() ===
      ExpressionDataFunction.firstBlockInCase.toLowerCase()
    ) {
      result.push(
        getDataBlockQuery(func.args[0], ExpressionDataFunction.firstBlockInCase)
      );
    } else if (
      func.functionName.toLowerCase() ===
      ExpressionDataFunction.lastBlockInCase.toLowerCase()
    ) {
      result.push(
        getDataBlockQuery(func.args[0], ExpressionDataFunction.lastBlockInCase)
      );
    } else if (
      func.functionName.toLowerCase() ===
      ExpressionDataFunction.firstVisitInCase.toLowerCase()
    ) {
      result.push({
        query: {
          queryType: MergeFieldQueryType.VisitQuery,
          dataBlockTypeName: null,
          selectedVisits: selectedVisits,
        },
        fields: [],
        filter: '',
        limit: ExpressionDataFunction.firstVisitInCase,
        expressionFunctionName: ExpressionDataFunction.firstVisitInCase,
        expressionFunctionArgument: '',
      });
    } else if (
      func.functionName.toLowerCase() ===
      ExpressionDataFunction.lastVisitInCase.toLowerCase()
    ) {
      result.push({
        query: {
          queryType: MergeFieldQueryType.VisitQuery,
          dataBlockTypeName: null,
          selectedVisits: selectedVisits,
        },
        fields: [],
        filter: '',
        limit: ExpressionDataFunction.lastVisitInCase,
        expressionFunctionName: ExpressionDataFunction.lastVisitInCase,
        expressionFunctionArgument: '',
      });
    } else if (
      func.functionName.toLowerCase() ===
      ExpressionDataFunction.previousVisit.toLowerCase()
    ) {
      result.push({
        query: {
          queryType: MergeFieldQueryType.VisitQuery,
          dataBlockTypeName: null,
          selectedVisits: selectedVisits,
        },
        fields: [],
        filter: '',
        limit: ExpressionDataFunction.previousVisit,
        expressionFunctionName: ExpressionDataFunction.previousVisit,
        expressionFunctionArgument: '',
      });
    } else if (
      func.functionName.toLowerCase() ===
      ExpressionDataFunction.nextVisit.toLowerCase()
    ) {
      result.push({
        query: {
          queryType: MergeFieldQueryType.VisitQuery,
          dataBlockTypeName: null,
        },
        fields: [],
        filter: '',
        limit: ExpressionDataFunction.nextVisit,
        expressionFunctionName: ExpressionDataFunction.nextVisit,
        expressionFunctionArgument: '',
      });
    } else if (
      func.functionName.toLowerCase() ===
      ExpressionDataFunction.selectedVisits.toLowerCase()
    ) {
      if (!selectedVisits) {
        if (!_selectVisitsFunction) {
          selectedVisits = [];
        } else {
          selectedVisits = await _selectVisitsFunction();
        }
      }
      result.push({
        query: {
          queryType: MergeFieldQueryType.VisitQuery,
          dataBlockTypeName: null,
          selectedVisits: selectedVisits,
        },
        fields: [],
        filter: '',
        limit: ExpressionDataFunction.selectedVisits,
        expressionFunctionName: ExpressionDataFunction.selectedVisits,
        expressionFunctionArgument: '',
      });
    } else if (
      func.functionName.toLowerCase() ===
      ExpressionDataFunction.allVisits.toLowerCase()
    ) {
      result.push({
        query: {
          queryType: MergeFieldQueryType.VisitQuery,
          dataBlockTypeName: null,
          selectedVisits: selectedVisits,
        },
        fields: [],
        filter: '',
        limit: ExpressionDataFunction.allVisits,
        expressionFunctionName: ExpressionDataFunction.allVisits,
        expressionFunctionArgument: '',
      });
    } else if (
      func.functionName.toLowerCase() ===
      ExpressionDataFunction.previousBlock.toLowerCase()
    ) {
      result.push(
        getDataBlockQuery(func.args[0], ExpressionDataFunction.previousBlock)
      );
    } else if (
      func.functionName.toLowerCase() ===
      ExpressionDataFunction.firstBlockInVisit.toLowerCase()
    ) {
      result.push(
        getDataBlockQuery(
          func.args[0],
          ExpressionDataFunction.firstBlockInVisit
        )
      );
    } else if (
      func.functionName.toLowerCase() ===
      ExpressionDataFunction.lastBlockInVisit.toLowerCase()
    ) {
      result.push(
        getDataBlockQuery(func.args[0], ExpressionDataFunction.lastBlockInVisit)
      );
    } else if (
      func.functionName.toLowerCase() ===
      ExpressionDataFunction.nthBlockInVisit.toLowerCase()
    ) {
      result.push(
        getDataBlockQuery(
          func.args[0],
          ExpressionDataFunction.nthBlockInVisit,
          func.args[1]
        )
      );
    } else if (
      func.functionName.toLowerCase() ===
      ExpressionDataFunction.nthBlockInCase.toLowerCase()
    ) {
      result.push(
        getDataBlockQuery(
          func.args[0],
          ExpressionDataFunction.nthBlockInCase,
          func.args[1]
        )
      );
    } else if (
      func.functionName.toLowerCase() ===
      ExpressionDataFunction.block.toLowerCase()
    ) {
      const fields = visitor.getMembers
        .filter((x) => x.leftPart === `block(${func.args[0]})`)
        .map((x) => x.memberName);
      result.push(
        getDataBlockQuery(
          func.args[0],
          ExpressionDataFunction.block,
          undefined,
          fields
        )
      );
    } else if (
      func.functionName.toLowerCase() ===
      ExpressionDataFunction.allBlocksInCase.toLowerCase()
    ) {
      result.push(
        getDataBlockQuery(func.args[0], ExpressionDataFunction.allBlocksInCase)
      );
    } else if (
      func.functionName.toLowerCase() ===
      ExpressionDataFunction.allBlocksInVisit.toLowerCase()
    ) {
      result.push(
        getDataBlockQuery(func.args[0], ExpressionDataFunction.allBlocksInVisit)
      );
    } else if (
      func.functionName.toLowerCase() ===
      ExpressionDataFunction.hl7.toLowerCase()
    ) {
      result.push({
        query: {
          queryType: MergeFieldQueryType.HL7Settings,
          dataBlockTypeName: null,
        },
        fields: [],
        filter: '',
        limit: ExpressionDataFunction.hl7,
        expressionFunctionName: ExpressionDataFunction.hl7,
        expressionFunctionArgument: '',
      });
    } else if (
      func.functionName.toLowerCase() ===
      ExpressionDataFunction.gdt.toLowerCase()
    ) {
      result.push({
        query: {
          queryType: MergeFieldQueryType.GDTSettings,
          dataBlockTypeName: null,
        },
        fields: [],
        filter: '',
        limit: ExpressionDataFunction.gdt,
        expressionFunctionName: ExpressionDataFunction.gdt,
        expressionFunctionArgument: '',
      });
    } else if (
      func.functionName.toLowerCase() ===
      ExpressionDataFunction.file.toLowerCase()
    ) {
      result.push({
        query: {
          queryType: MergeFieldQueryType.FileQuery,
          dataBlockTypeName: null,
        },
        fields: [],
        filter: '',
        limit: ExpressionDataFunction.file,
        expressionFunctionName: ExpressionDataFunction.file,
        expressionFunctionArgument: '',
      });
    }
  }

  const distinct = distinctQueries(result);
  return distinct;
}

export function distinctQueries(
  allQueries: QueryWithParameters[]
): QueryWithParameters[] {
  const existingQueries: Record<string, boolean> = {};
  return allQueries.filter((query) => {
    const queryAsString = [
      query.query.queryType,
      query.query.dataBlockTypeName,
      query.filter,
      query.limit,
      query.expressionFunctionArgument2,
    ].join('$#$');
    if (existingQueries[queryAsString]) {
      return false;
    }
    existingQueries[queryAsString] = true;
    return true;
  });
}

function getDataBlockQuery(
  dataBlockTypeName: string,
  limit: ExpressionDataFunction,
  argument2?: string,
  fields?: string[]
): QueryWithParameters {
  return {
    query: {
      queryType: MergeFieldQueryType.DataBlockQuery,
      dataBlockTypeName: dataBlockTypeName,
      selectedVisits: null,
    },
    filter: null,
    limit: limit,
    fields: fields ?? [],
    expressionFunctionName: limit,
    expressionFunctionArgument: dataBlockTypeName,
    expressionFunctionArgument2: argument2,
  };
}
